import { z } from 'zod';
import { amountMinorSchema, isoTimestampSchema, localeSchema, uuidSchema } from './common';
import { menuItemImageSchema } from './catalog';

/*
 * Public guest QR routes (ADR-0023). A table QR code carries an opaque table token; everything
 * here is scoped to that table's branch (menu, promotions) or its current table session (bill,
 * online payment). These DTOs deliberately omit staff-only fields (stations, versions, users).
 */

/** Opaque token from the table's QR code. */
export const tableTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{6,64}$/);

export const guestModifierGroupSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  min_select: z.number().int(),
  max_select: z.number().int(),
  options: z.array(
    z.object({ id: uuidSchema, name: z.string(), price_delta_minor: amountMinorSchema }),
  ),
});

export const guestMenuItemSchema = z.object({
  id: uuidSchema,
  category_id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  price_minor: amountMinorSchema,
  image: menuItemImageSchema.nullable(),
  /** false = sold out today. */
  available: z.boolean(),
  modifier_group_ids: z.array(uuidSchema),
});
export type GuestMenuItem = z.infer<typeof guestMenuItemSchema>;

export const guestPromotionSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  badge: z.string().nullable(),
  image_url: z.string().nullable(),
  /** Jump target on the menu; null when the promotion is not about one item. */
  menu_item_id: uuidSchema.nullable(),
  ends_at: isoTimestampSchema.nullable(),
});
export type GuestPromotion = z.infer<typeof guestPromotionSchema>;

export const guestMenuSchema = z.object({
  branch: z.object({
    name: z.string(),
    currency: z.string(),
    locale: localeSchema,
    time_zone: z.string(),
    service_charge_bps: z.number().int(),
    tax_rate_bps: z.number().int(),
  }),
  table: z.object({ name: z.string() }),
  categories: z.array(z.object({ id: uuidSchema, name: z.string() })),
  items: z.array(guestMenuItemSchema),
  modifier_groups: z.array(guestModifierGroupSchema),
  promotions: z.array(guestPromotionSchema),
  /** Whether "pay in the app" is offered (a payment provider is configured). */
  online_payment_enabled: z.boolean(),
  /** Whether guests may order from this menu (plan includes guest ordering). Absent = true. */
  ordering_enabled: z.boolean().optional(),
  /** Tenant branding for the menu header (ADR-0036). */
  brand: z
    .object({ display_name: z.string().nullable(), logo_url: z.string().nullable() })
    .optional(),
});
export type GuestMenu = z.infer<typeof guestMenuSchema>;

export const ONLINE_PAYMENT_STATUSES = [
  'pending',
  'settled',
  'failed',
  'expired',
  'cancelled',
  'refund_required',
] as const;
export const onlinePaymentStatusSchema = z.enum(ONLINE_PAYMENT_STATUSES);
export type OnlinePaymentStatus = z.infer<typeof onlinePaymentStatusSchema>;

export const guestOnlinePaymentSchema = z.object({
  id: uuidSchema,
  status: onlinePaymentStatusSchema,
  amount_minor: amountMinorSchema,
  currency: z.string(),
  /** Provider checkout page; only while pending. */
  checkout_url: z.string().nullable(),
  expires_at: isoTimestampSchema,
  created_at: isoTimestampSchema,
  settled_at: isoTimestampSchema.nullable(),
});
export type GuestOnlinePayment = z.infer<typeof guestOnlinePaymentSchema>;

export const guestBillLineSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  modifiers: z.array(z.string()),
  quantity: z.number().int(),
  line_total_minor: amountMinorSchema,
  status: z.enum(['sent', 'preparing', 'ready', 'served']),
});

export const guestBillSchema = z.object({
  /** null when nobody has seated this table yet (no open table session). */
  session: z
    .object({
      id: uuidSchema,
      status: z.enum(['open', 'payment_pending']),
      opened_at: isoTimestampSchema,
    })
    .nullable(),
  bill_number: z.string().nullable(),
  /** Items sent to the kitchen; drafts the waiter has not sent are not shown. */
  lines: z.array(guestBillLineSchema),
  subtotal_minor: amountMinorSchema,
  service_charge_minor: amountMinorSchema,
  tax_minor: amountMinorSchema,
  rounding_minor: amountMinorSchema,
  total_minor: amountMinorSchema,
  paid_minor: amountMinorSchema,
  outstanding_minor: amountMinorSchema,
  service_charge_bps: z.number().int(),
  tax_rate_bps: z.number().int(),
  /** True while totals are computed from items not yet on the bill (the bill is made on pay). */
  is_estimate: z.boolean(),
  pending_payment: guestOnlinePaymentSchema.nullable(),
  online_payment_enabled: z.boolean(),
});
export type GuestBill = z.infer<typeof guestBillSchema>;

/**
 * Start paying the table's outstanding balance online. The guest confirms the amount they saw;
 * if the bill changed meanwhile the server refuses (`amount_changed`) so they can review it.
 */
export const createGuestPaymentRequestSchema = z.object({
  table_session_id: uuidSchema,
  expected_amount_minor: amountMinorSchema.refine((v) => v > 0, 'Must be positive'),
});

// ---- Payment simulator (local/staging stand-in for a real provider; never production)

export const simulatorCheckoutSchema = z.object({
  reference: z.string(),
  merchant_name: z.string(),
  amount_minor: amountMinorSchema,
  currency: z.string(),
  status: onlinePaymentStatusSchema,
  expires_at: isoTimestampSchema,
  /** Where the provider sends the guest back (the table's bill page). */
  return_url: z.string(),
});
export type SimulatorCheckout = z.infer<typeof simulatorCheckoutSchema>;

export const simulatorCompleteRequestSchema = z.object({ outcome: z.enum(['paid', 'failed']) });

// ---- Guest self-order (QR) ---------------------------------------------------------------

/** Abuse limits for one guest submission (ADR-0029). */
export const GUEST_ORDER_LIMITS = {
  maxLines: 20,
  maxQuantityPerLine: 20,
  maxPortions: 40,
} as const;

export const guestOrderLineInputSchema = z.object({
  menu_item_id: z.uuid(),
  quantity: z.number().int().min(1).max(GUEST_ORDER_LIMITS.maxQuantityPerLine),
  modifier_option_ids: z.array(z.uuid()).max(20).default([]),
  notes: z.string().trim().max(120).nullable().default(null),
});

/**
 * Guest order from the table QR: priced by the server and sent straight to the kitchen. If the
 * table has no open session yet, it is opened with `guest_count`.
 */
export const createGuestOrderRequestSchema = z
  .object({
    items: z.array(guestOrderLineInputSchema).min(1).max(GUEST_ORDER_LIMITS.maxLines),
    guest_count: z.number().int().min(1).max(30).optional(),
  })
  .refine((v) => v.items.reduce((n, i) => n + i.quantity, 0) <= GUEST_ORDER_LIMITS.maxPortions, {
    message: `At most ${GUEST_ORDER_LIMITS.maxPortions} portions per order.`,
    path: ['items'],
  });
export type CreateGuestOrderRequest = z.input<typeof createGuestOrderRequestSchema>;

export const guestOrderItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  quantity: z.number().int(),
  modifiers: z.array(z.string()),
  notes: z.string().nullable(),
  unit_price_minor: z.number().int(),
  line_total_minor: z.number().int(),
  /** Kitchen progress; `cancelled` items were removed by staff. */
  status: z.enum(['sent', 'preparing', 'ready', 'served', 'cancelled']),
  sent_at: z.string().nullable(),
});

/** The table's current order as the guest sees it (no staff fields). */
export const guestOrderSchema = z.object({
  order_number: z.string(),
  table_name: z.string(),
  currency: z.string(),
  items: z.array(guestOrderItemSchema),
  /** Non-cancelled items, before service charge and tax (the bill adds those). */
  ordered_total_minor: z.number().int(),
});
export type GuestOrder = z.infer<typeof guestOrderSchema>;

export const guestOrderResponseSchema = z.object({ order: guestOrderSchema.nullable() });
